import { toast } from '@neo4j-ndl/react';

import { customCreateApi } from '../../context';
import { createDynamicBaseQuery } from '../../utils/create-dynamic-base-query';
import type { DesktopState } from './desktop';
import { dbEndpoints } from './endpoints/db';
import { dbmsEndpoints } from './endpoints/dbms';
import { eventEndpoints } from './endpoints/events';
import { pluginEndpoints } from './endpoints/plugin';
import { remoteConnectionEndpoints } from './endpoints/remote-connection';
import { type Builder, tagTypes } from './endpoints/types';
import type { WatchUpdateAvailableResult } from './relate.subscriptions';
import { WATCH_UPDATE_AVAILABLE, registerSubscription } from './relate.subscriptions';
import type {
  CredentialsType,
  DbmsInfo,
  DesktopV1CloneStatus,
  DesktopV1Info,
  ElectronAppInfo,
  SelectedFile,
  UserSettings,
} from './relate.types';

const isDesktopState = (state: unknown): state is { desktop: DesktopState } => {
  if (state !== null && typeof state === 'object' && 'desktop' in state) {
    return true;
  }
  return false;
};

const baseQuery = createDynamicBaseQuery(
  ({ desktop }) => `${desktop.relate.protocol ?? ''}${desktop.relate.url ?? ''}`,
  {
    prepareHeaders: (headers, { getState }) => {
      const state = getState();
      if (isDesktopState(state)) {
        const { apiToken, clientId } = state.desktop.relate;

        if (apiToken !== undefined) {
          headers.set('X-API-Token', apiToken);
        }

        if (clientId !== undefined) {
          headers.set('X-Client-Id', clientId);
        }
      }

      return headers;
    },
  },
);

const relateApi = customCreateApi({
  baseQuery,
  reducerPath: 'relateApi',
  tagTypes,
  endpoints: (builder: Builder) => ({
    ...dbmsEndpoints(builder),
    ...dbEndpoints(builder),
    ...pluginEndpoints(builder),
    ...remoteConnectionEndpoints(builder),
    ...eventEndpoints(builder),

    appInfo: builder.query<ElectronAppInfo, void>({
      query: () => 'app-info',
    }),

    getCredentials: builder.query<{ username: string; password: string } | null, { id: string; type: CredentialsType }>(
      {
        query: ({ id, type }) => ({
          url: 'get-credentials',
          params: { key: `${type}:${id}` },
        }),
        providesTags: (_res, _err, { id }) => [{ type: 'Credentials', id }],
      },
    ),
    setCredentials: builder.mutation<
      boolean,
      { id: string; type: CredentialsType; username: string; password: string }
    >({
      query: ({ id, type, username, password }) => ({
        url: 'set-credentials',
        method: 'POST',
        body: { key: `${type}:${id}`, username, password },
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'Credentials', id }],
    }),

    openDirectory: builder.mutation<void, string[]>({
      query: (filePath: string[]) => ({
        url: 'open-directory',
        method: 'POST',
        body: { filePath },
      }),
    }),
    openFile: builder.mutation<void, string[]>({
      query: (filePath: string[]) => ({
        url: 'open-file',
        method: 'POST',
        body: { filePath },
      }),
    }),

    openDialog: builder.mutation<
      SelectedFile | null,
      {
        title?: string;
        properties?: (
          | 'openFile'
          | 'openDirectory'
          | 'multiSelections'
          | 'showHiddenFiles'
          | 'createDirectory'
          | 'promptToCreate'
          | 'noResolveAliases'
          | 'treatPackageAsDirectory'
          | 'dontAddToRecent'
        )[];
        message?: string;
        defaultPath?: string[];
      }
    >({
      query: (options) => ({
        url: 'open-dialog',
        method: 'POST',
        body: { options },
      }),
    }),

    getUserSettings: builder.query<UserSettings, void>({
      query: () => ({
        url: 'get-user-settings',
      }),
      providesTags: ['UserSettings'],
    }),

    setUserSettings: builder.mutation<boolean, Partial<UserSettings>>({
      query: (settingsToChange) => ({
        url: 'set-user-settings',
        method: 'POST',
        body: settingsToChange,
      }),
      invalidatesTags: ['UserSettings'],
    }),

    downloadUpdate: builder.mutation<boolean, void>({
      query: () => ({
        url: 'download-update',
        method: 'POST',
      }),
    }),

    restartAndUpdate: builder.mutation<boolean, void>({
      query: () => ({
        url: 'restart-and-update',
        method: 'POST',
      }),
    }),

    updateAvailable: builder.query<WatchUpdateAvailableResult['updateAvailable'] | null, void>({
      query: () => ({ url: 'update-available' }),

      onCacheEntryAdded(_arg, api) {
        return registerSubscription<WatchUpdateAvailableResult>(api, WATCH_UPDATE_AVAILABLE, ({ data }) => {
          api.updateCachedData((draft) => {
            return data?.updateAvailable ?? null;
          });
        });
      },
    }),

    desktopV1Info: builder.query<DesktopV1Info, void>({
      query: () => ({ url: 'desktop-v1-info' }),
    }),
    cloneDesktopV1Dbmss: builder.mutation<DesktopV1CloneStatus[], DbmsInfo[]>({
      query: (dbmssInfo) => ({
        url: 'clone-desktop-v1-dbmss',
        method: 'POST',
        body: { dbmsIds: dbmssInfo.map((dbms) => dbms.id) },
      }),

      async onQueryStarted(dbmssInfo, api) {
        try {
          const { data } = await api.queryFulfilled;
          const failedToClone = data.filter((res) => res.status === 'rejected');
          if (failedToClone.length > 0) {
            toast.danger(`Error cloning instance: ${failedToClone[0]?.reason}`, {
              actionLabel: 'Retry',
              onAction: () => {
                void api.dispatch(
                  relateApi.endpoints.cloneDesktopV1Dbmss.initiate(
                    dbmssInfo.filter((dbms) => failedToClone.find((failed) => failed.sourceId === dbms.id)),
                  ),
                );
              },
              shouldCloseOnAction: true,
            });
          }
        } catch (err) {
          toast.danger(`Error cloning instance: ${err instanceof Error ? err.message : String(err)}`, {
            actionLabel: 'Retry',
            onAction: () => {
              void api.dispatch(relateApi.endpoints.cloneDesktopV1Dbmss.initiate(dbmssInfo));
            },
            shouldCloseOnAction: true,
          });
        }
      },

      invalidatesTags: ['Dbms'],
    }),
  }),
});

export const {
  /**
   * @deprecated use `useDbmsList` instead.
   */
  useListDbmssQuery,
  useListNeo4jVersionsQuery,
  useStartDbmsMutation,
  useStopDbmsMutation,
  useInstallDbmsMutation,
  useUninstallDbmsMutation,
  useCloneDbmsMutation,
  useUpgradeDbmsMutation,
  useUpdateDbmsNameMutation,
  useWatchProgressQuery,

  useListDbConnectionsQuery,
  useCreateDbConnectionMutation,
  useUpdateDbConnectionMutation,
  useRemoveDbConnectionMutation,

  useListDatabasesQuery,
  useCreateDatabaseMutation,
  useDropDatabaseMutation,
  useDumpDatabaseMutation,
  useLoadDatabaseMutation,
  useRestoreBackupMutation,

  useListInstalledPluginsQuery,
  useListAvailablePluginsQuery,
  useInstallPluginMutation,
  useUninstallPluginMutation,
  useOpenDirectoryMutation,
  useOpenFileMutation,

  useOpenDialogMutation,
  useAppInfoQuery,

  useGetCredentialsQuery,
  useLazyGetCredentialsQuery,
  useSetCredentialsMutation,

  useGetUserSettingsQuery,
  useSetUserSettingsMutation,

  useDownloadUpdateMutation,
  useRestartAndUpdateMutation,
  useUpdateAvailableQuery,

  useDesktopV1InfoQuery,
  useCloneDesktopV1DbmssMutation,
} = relateApi;

// TODO: add relateApi to the main Store, for the Bloom Standalone release we remove it as it was making a hard coded
// request to http://localhost:4000 which might be a problem for our customer. Enable it again when we can dynamically
// load the state so we can disable this part when we are building Bloom Standalone
export default relateApi;
